/**
 * API Client
 *
 * Native fetch-based client (no axios)
 * Based on rule-frontend.md Section 6.1
 */

import { ApiError } from './errors'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

/**
 * API Response type (matches backend contract)
 */
export interface ApiResponse<T = unknown> {
  data: T
  meta?: {
    page?: number
    perPage?: number
    total?: number
    totalPages?: number
    classification?: string
    minN?: number
  }
  requestId: string
}

/**
 * Fetcher function for SWR
 *
 * Usage: useSWR('/api/endpoint', apiClient.fetcher)
 */
export async function fetcher<T = unknown>(
  url: string | [string, Record<string, unknown>]
): Promise<T> {
  const [path, params] = Array.isArray(url) ? url : [url, undefined]

  const fullUrl = params
    ? `${BASE_URL}${path}?${new URLSearchParams(params as Record<string, string>)}`
    : `${BASE_URL}${path}`

  // Get auth token from localStorage
  const token = localStorage.getItem('access_token')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(fullUrl, {
    credentials: 'include', // Send cookies (httpOnly auth token)
    headers,
  })

  // Parse JSON
  const json: ApiResponse<T> = await response.json()

  // Handle error responses
  if (!response.ok) {
    throw await ApiError.fromResponse(response)
  }

  // Return data field (SWR expects just the data, not the wrapper)
  return json.data
}

/**
 * POST request
 */
export async function post<T = unknown>(
  path: string,
  body: unknown
): Promise<ApiResponse<T>> {
  try {
    const token = localStorage.getItem('access_token')

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    })

    const json: ApiResponse<T> = await response.json()

    if (!response.ok) {
      throw await ApiError.fromResponse(response)
    }

    return json
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    // Network error (no response)
    throw ApiError.networkError()
  }
}

/**
 * PUT request
 */
export async function put<T = unknown>(
  path: string,
  body: unknown
): Promise<ApiResponse<T>> {
  try {
    const token = localStorage.getItem('access_token')

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    })

    const json: ApiResponse<T> = await response.json()

    if (!response.ok) {
      throw await ApiError.fromResponse(response)
    }

    return json
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    throw ApiError.networkError()
  }
}

/**
 * PATCH request
 */
export async function patch<T = unknown>(
  path: string,
  body: unknown
): Promise<ApiResponse<T>> {
  try {
    const token = localStorage.getItem('access_token')

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'PATCH',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    })

    const json: ApiResponse<T> = await response.json()

    if (!response.ok) {
      throw await ApiError.fromResponse(response)
    }

    return json
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    throw ApiError.networkError()
  }
}

/**
 * DELETE request
 */
export async function del<T = unknown>(
  path: string
): Promise<ApiResponse<T>> {
  try {
    const token = localStorage.getItem('access_token')

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'DELETE',
      credentials: 'include',
      headers,
    })

    const json: ApiResponse<T> = await response.json()

    if (!response.ok) {
      throw await ApiError.fromResponse(response)
    }

    return json
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    throw ApiError.networkError()
  }
}

/**
 * API Client object
 */
export const apiClient = {
  fetcher,
  post,
  put,
  patch,
  delete: del,
}

export default apiClient
